import { VOICE_NOTE_MAX_SECONDS, voiceNoteLimitReached } from './policy';

/**
 * Ses kaydı cihazı (9.1 · D-541).
 *
 * `expo-audio` native bir modül. 20.09.2026 derlemesinde var, ama native modül
 * ikiliye ancak derlemeyle girer — OTA ile değil. Daha eski bir ikili hâlâ
 * çalışmalı; o yüzden modül dosya yüklenirken değil, kayıt başlarken isteniyor. Aynı gerekçe `calls/media.native.ts` ve
 * `media/captureLocation.ts` için de geçerliydi; üçüncü bir desen açılmıyor.
 *
 * `voiceRecordingAvailable` false iken giriş noktası hiç çizilmez: basılınca
 * çöken bir düğme yerine görünmeyen bir düğme doğrudur (9.4.4.3.1'deki aynı
 * karar).
 */
/**
 * Buradaki her ad, `expo-audio`'nun ÇALIŞMA ANINDA dışa verdiği bir addır ve
 * `expoAudioSurface.test.ts` bunu pakete bağlar. Önce `createAudioRecorder`,
 * sonra `AudioRecorder` yazılmıştı; ilki paketin hiçbir yerinde yok, ikincisi
 * yalnız TİP olarak var — sınıfın kendisi dahilî `AudioModule`'de duruyor.
 * İkisinde de `start()` cihazda sessizce false dönüyordu ve testler geçiyordu,
 * çünkü taklit paketin vermediği adı uyduruyordu.
 *
 * Paketin verdiği tek desteklenen zorunlu yol `useAudioRecorder` kancası; o
 * yüzden cihazı YARATMA işi bu modülden çıkıp `useNativeRecorder`'a taşındı.
 * Kurallar — tavan, boş kayıt, iptal — burada kaldı ve testlenebilir durumda.
 */
type AudioModule = {
  requestRecordingPermissionsAsync?: () => Promise<{ granted: boolean }>;
  RecordingPresets?: Record<string, unknown>;
  useAudioRecorder?: (options: unknown) => AnyRecorder;
  setAudioModeAsync?: (mode: unknown) => Promise<void>;
};
type AnyRecorder = {
  prepareToRecordAsync: () => Promise<void>;
  record: () => void;
  stop: () => Promise<void>;
  uri?: string | null;
  currentTime?: number;
};

function yukle(): AudioModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-audio') as AudioModule;
  } catch {
    return null;
  }
}

const modulRef = yukle();

export const voiceRecordingAvailable =
  typeof modulRef?.useAudioRecorder === 'function' && modulRef?.RecordingPresets != null;

/**
 * Native cihazı üretir. React kancası olmak zorunda: paket imperatif bir
 * fabrika vermiyor, sınıfı da dışa vermiyor. Modül yokken kanca hiç
 * çağrılmıyor — bu bir koşul gibi görünse de süreç boyunca değişmeyen bir
 * sabit, yani çizimler arasında kanca sırası kaymıyor.
 */
export function useNativeRecorder(): AnyRecorder | null {
  const modul = modulRef;
  if (!modul?.useAudioRecorder || !modul.RecordingPresets) return null;
  return modul.useAudioRecorder(modul.RecordingPresets.HIGH_QUALITY);
}

export type VoiceRecording = {
  /** Cihazdaki geçici dosya; yükleme katmanı bunu okur. */
  uri: string;
  /** Kaydın gerçek uzunluğu. Ücret bundan hesaplanır, sınırda kesilir. */
  seconds: number;
};

export type VoiceRecorder = {
  start(): Promise<boolean>;
  /** Kaydı bitirir. Hiç ses alınamadıysa null döner — boş kayıt gönderilmez. */
  stop(): Promise<VoiceRecording | null>;
  /** Kaydı atar; dosya gönderilmez ve ücret işlemez. */
  cancel(): Promise<void>;
  /** Geçen süreyi saniyede bir bildirir; sınıra varınca kendi durur. */
  onTick(cb: (seconds: number) => void): void;
};

export async function requestMicrophonePermission(): Promise<boolean> {
  const modul = yukle();
  if (!modul) return false;
  try {
    const iste = modul.requestRecordingPermissionsAsync;
    if (!iste) return false;
    return (await iste()).granted === true;
  } catch {
    return false;
  }
}

export function createVoiceRecorder(native: AnyRecorder | null): VoiceRecorder {
  let recorder: AnyRecorder | null = null;
  let sayac: ReturnType<typeof setInterval> | null = null;
  let saniye = 0;
  let tick: ((seconds: number) => void) | null = null;
  let kendiDurdu = false;

  const sayaciDurdur = () => {
    if (sayac) { clearInterval(sayac); sayac = null; }
  };

  return {
    onTick(cb) { tick = cb; },

    async start() {
      if (!native) return false;
      try {
        if (!await requestMicrophonePermission()) return false;
        // Kayıt sırasında sesin cihazda doğru yönlenmesi için mod açıkça
        // kuruluyor; varsayılan bırakılırsa iOS'ta kayıt sessiz çıkabiliyor.
        await modulRef?.setAudioModeAsync?.({ allowsRecording: true, playsInSilentMode: true });
        recorder = native;
        await recorder.prepareToRecordAsync();
        recorder.record();
        saniye = 0;
        kendiDurdu = false;
        sayac = setInterval(() => {
          saniye += 1;
          tick?.(saniye);
          // Sınır burada da uygulanıyor, yalnız arayüzde değil: kaydı durduran
          // şey kullanıcının parmağı olmayabilir (uygulama arkaya düşer,
          // ekran kilitlenir). Ücretin tavanı kaydın kendisinde durmalı.
          if (voiceNoteLimitReached(saniye)) {
            kendiDurdu = true;
            sayaciDurdur();
            void recorder?.stop();
          }
        }, 1000);
        return true;
      } catch {
        recorder = null;
        sayaciDurdur();
        return false;
      }
    },

    async stop() {
      sayaciDurdur();
      if (!recorder) return null;
      try {
        if (!kendiDurdu) await recorder.stop();
        const uri = recorder.uri ?? null;
        const uzunluk = Math.min(saniye, VOICE_NOTE_MAX_SECONDS);
        recorder = null;
        // Süresi sıfır olan veya dosyası oluşmayan kayıt gönderilmez: boş bir
        // ses mesajı alıcıya bir şey söylemez ama ücret işletirdi.
        if (!uri || uzunluk <= 0) return null;
        return { uri, seconds: uzunluk };
      } catch {
        recorder = null;
        return null;
      }
    },

    async cancel() {
      sayaciDurdur();
      const acik = recorder;
      recorder = null;
      saniye = 0;
      if (!acik || kendiDurdu) return;
      try { await acik.stop(); } catch { /* atılan kayıt için hata yutulur */ }
    },
  };
}
